'use client';

import Link from 'next/link';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Shield, FileText, Scale, AlertTriangle, Mail, ExternalLink } from 'lucide-react';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const privacyLinks = [
    {
      href: '/public/privacy/privacy-policy',
      label: 'Privacy Policy',
      icon: Shield,
      description: 'How we protect your data'
    },
    {
      href: '/public/privacy/cookie-policy',
      label: 'Cookie Policy',
      icon: FileText,
      description: 'Our cookie usage'
    },
    {
      href: '/public/privacy/terms-of-service',
      label: 'Terms of Service',
      icon: Scale,
      description: 'Service agreement'
    },
    {
      href: '/public/privacy/legal-notice',
      label: 'Legal Notice',
      icon: AlertTriangle,
      description: 'Legal information'
    }
  ];

  return (
    <footer className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800">
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-linear-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">LZ</span>
              </div>
              <div>
                <h3 className="font-bold text-lg bg-linear-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  LitePop
                </h3>
                <p className="text-xs text-muted-foreground">by DeepZone</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Creating innovative solutions with cutting-edge technology and user-centric design.
            </p>
            <Badge variant="outline" className="w-fit">
              <Mail className="h-3 w-3 mr-1" />
              privacy@deepzone.com
            </Badge>
          </div>

          {/* Privacy & Legal Links */}
          <div className="space-y-4">
            <h4 className="font-semibold text-sm text-slate-900 dark:text-slate-100">
              Privacy & Legal
            </h4>
            <nav className="space-y-3">
              {privacyLinks.map((link) => {
                const IconComponent = link.icon;
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="group flex items-start gap-3 text-sm text-muted-foreground hover:text-slate-900 dark:hover:text-slate-100 transition-colors duration-200"
                  >
                    <IconComponent className="h-4 w-4 mt-0.5 text-blue-500 group-hover:text-blue-600 transition-colors" />
                    <div>
                      <div className="font-medium group-hover:underline">
                        {link.label}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {link.description}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="font-semibold text-sm text-slate-900 dark:text-slate-100">
              Quick Links
            </h4>
            <nav className="space-y-3">
              <Link
                href="/"
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-slate-900 dark:hover:text-slate-100 transition-colors duration-200"
              >
                <ExternalLink className="h-3 w-3" />
                <span className="hover:underline">Home</span>
              </Link>
              <Link
                href="/about"
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-slate-900 dark:hover:text-slate-100 transition-colors duration-200"
              >
                <ExternalLink className="h-3 w-3" />
                <span className="hover:underline">About</span>
              </Link>
              <Link
                href="/contact"
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-slate-900 dark:hover:text-slate-100 transition-colors duration-200"
              >
                <ExternalLink className="h-3 w-3" />
                <span className="hover:underline">Contact</span>
              </Link>
            </nav>
          </div>

          {/* Support */}
          <div className="space-y-4">
            <h4 className="font-semibold text-sm text-slate-900 dark:text-slate-100">
              Support
            </h4>
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                <p className="font-medium mb-1">Need Help?</p>
                <p className="text-xs leading-relaxed">
                  Contact our support team for assistance with any questions or concerns.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <a
                  href="mailto:privacy@deepzone.com"
                  className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 transition-colors"
                >
                  <Mail className="h-3 w-3" />
                  <span className="hover:underline">Email Support</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <Separator className="my-8" />

        {/* Bottom Footer */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-sm text-muted-foreground">
            <p>© {currentYear} DeepZone. All rights reserved.</p>
          </div>
          
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>Built with</span>
            <Badge variant="secondary" className="text-xs">
              Next.js
            </Badge>
            <Badge variant="secondary" className="text-xs">
              Shadcn UI
            </Badge>
            <Badge variant="secondary" className="text-xs">
              TypeScript
            </Badge>
          </div>
        </div>

        {/* Legal Disclaimer */}
        <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800">
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            This website and its content are protected by intellectual property laws. 
            By using this service, you agree to our terms and conditions. 
            For questions about our privacy practices, please review our privacy policy or contact us directly.
          </p>
        </div>
      </div>
    </footer>
  );
}